/**
 * Raw WebSocket Transport Implementation
 *
 * Direct WebSocket connection with automatic reconnection.
 * Lowest-level transport with minimal overhead.
 * No built-in message serialization - raw bytes only.
 */

import { BaseTransport } from './BaseTransport';
import type { TransportConfig } from '../types';
import { calculateRetryDelay, delay } from '../utils';

export class RawWebSocketTransport extends BaseTransport {
  private ws: WebSocket | null = null;

  private reconnectAttempt = 0;

  private readonly wsProtocol: string;

  constructor(id: string, config: TransportConfig) {
    super(id, 'websocket', config);
    // Convert http:// to ws://, https:// to wss://
    this.wsProtocol = this.config.url.replace(/^http/, 'ws');
  }

  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.setStatus('connecting');

    return new Promise((resolve, reject) => {
      try {
        const url = new URL(this.wsProtocol);
        if (this.config.queryParams) {
          for (const [key, value] of Object.entries(this.config.queryParams)) {
            url.searchParams.set(key, value);
          }
        }

        this.ws = new WebSocket(url.toString());

        // Binary support
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          this.setStatus('connected');
          this.reconnectAttempt = 0;
          resolve();
        };

        this.ws.onmessage = (event: MessageEvent) => {
          this.emitMessage(event.data);
        };

        this.ws.onerror = () => {
          this.setStatus('error');
          this.emitError(new Error('WebSocket error'));
        };

        this.ws.onclose = () => {
          if (this.ws?.readyState === WebSocket.CLOSED) {
            if (this.status !== 'disconnected') {
              this.setStatus('reconnecting');
              this.reconnect().catch(reject);
            } else {
              this.setStatus('disconnected');
            }
          }
        };

        // Timeout for connection
        const timeout = this.config.timeoutMs || 10000;
        setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            this.ws?.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, timeout);
      } catch (error) {
        this.setStatus('error');
        this.emitError(error instanceof Error ? error : new Error(String(error)));
        reject(error);
      }
    });
  }

  async disconnect(): Promise<void> {
    if (this.ws) {
      try {
        this.ws.close();
      } catch (error) {
        this.logger.error('Error closing WebSocket', error as Error);
      }
      this.ws = null;
    }
    this.setStatus('disconnected');
    this.cleanup();
  }

  async send(data: string | ArrayBuffer): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket connection not open');
    }

    try {
      this.ws.send(data);
    } catch (error) {
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  private async reconnect(): Promise<void> {
    this.reconnectAttempt++;

    const delayMs = calculateRetryDelay(this.reconnectAttempt, {
      initialDelayMs: 1000,
      maxDelayMs: 30000,
      backoffMultiplier: 2,
      maxAttempts: 0,
    });

    if (delayMs) {
      await delay(delayMs);
      try {
        await this.connect();
      } catch (error) {
        if (error instanceof Error) {
          this.logger.debug(`WebSocket reconnect failed (attempt ${this.reconnectAttempt}), will retry`, error);
        }
        this.reconnect();
      }
    }
  }
}
